// apps/admin/src/features/billing/IssueCreditNoteModal.tsx
//
// Phase 8.2 Wave 3 Task 14 — Credit Note issuance modal (CN-YYYYMM-XXXXX).
//
// Per CONTEXT.md
//   • Invoice numbering format CN-YYYYMM-XXXXX (generated
//                  server-side; surfaced in success toast badge).
//   • Credit/Debit Note flow: admin chủ động tạo từ invoice
//                  detail (modal: amount + reason + line items selector);
//                  backend generates PDF + audit log.
//                  CN use cases: refund partial sau invoice gửi,
//                  correction over-charge.
//   • Audit log luôn có actor + reason; reason ≥10 chars.
//
// Per API-CONTRACTS.md §POST /api/v1/admin/invoices/:id/credit-notes
//   • Body: { amount_cents (>=1), reason (10..1000),
//             line_item_ids?: string[] }
//   • Response: { id, credit_note_number, invoice_id, amount_cents,
//                 pdf_url, issued_at, audit_log_id }
//   • Errors: 400 INVALID_BODY | 401 UNAUTHORIZED | 403 FORBIDDEN |
//             404 INVOICE_NOT_FOUND | 422 AMOUNT_EXCEEDS_INVOICE |
//             500 INTERNAL_ERROR.
//
// vg-binding: PLAN:task-14
// vg-binding: P8.D-455
// vg-binding: P8.D-456
// vg-binding: BR-11
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key

import { useEffect, useMemo, useState, type JSX } from 'react';

import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
  useToast,
} from '@printway/ui';

import { ApiError, apiClient } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import {
  AMOUNT_CENTS_MIN,
  REASON_MIN,
  formatUsdCentsCompact,
  newNoteIdempotencyKey,
  parseUsdDollarsToCents,
  NoteFormBase,
  type InvoiceLineItemSummary,
  type IssueCreditNoteResponse,
} from './NoteFormBase.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface IssueCreditNoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Invoice the credit note is being attached to. */
  invoiceId: string;
  /** Display label — invoice number or merchant name (top of modal). */
  invoiceLabel?: string;
  /** Total invoice amount (USD cents) — used for the 422 cap hint. */
  invoiceTotalCents?: number | null;
  /** Pre-populated line items from the invoice detail loader. */
  lineItems?: ReadonlyArray<InvoiceLineItemSummary>;
  /** Fired after successful issuance so the parent can invalidate cache. */
  onIssued?: (response: IssueCreditNoteResponse) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<IssueCreditNoteModal>` — issues a credit note linked to the parent
 * invoice. The form lives in `NoteFormBase` and this modal layers on
 * the multi-select line-item picker.
 */
export function IssueCreditNoteModal(
  props: IssueCreditNoteModalProps,
): JSX.Element {
  const {
    open,
    onOpenChange,
    invoiceId,
    invoiceLabel,
    invoiceTotalCents = null,
    lineItems = [],
    onIssued,
  } = props;

  const { toast } = useToast();
  const { t } = useTranslation('finance');

  const [amountDollars, setAmountDollars] = useState('');
  const [reason, setReason] = useState('');
  const [selectedLineItemIds, setSelectedLineItemIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);

  // Reset state every open — never bleed stale input from a prior issuance.
  useEffect(() => {
    if (open) {
      setAmountDollars('');
      setReason('');
      setSelectedLineItemIds([]);
      setSubmitting(false);
      setAmountError(null);
      setReasonError(null);
    }
  }, [open, invoiceId]);

  const lineItemMap = useMemo(() => {
    const map = new Map<string, InvoiceLineItemSummary>();
    for (const li of lineItems) map.set(li.id, li);
    return map;
  }, [lineItems]);

  function toggleLineItem(id: string): void {
    setSelectedLineItemIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function validateLocal(): {
    ok: boolean;
    amountCents?: number;
    trimmedReason?: string;
  } {
    let ok = true;

    const cents = parseUsdDollarsToCents(amountDollars);
    if (cents === null || cents < AMOUNT_CENTS_MIN) {
      setAmountError(t('invoice.creditNoteModal.amountInvalid'));
      ok = false;
    } else {
      setAmountError(null);
    }

    const trimmedReason = reason.trim();
    if (trimmedReason.length < REASON_MIN) {
      setReasonError(t('invoice.creditNoteModal.reasonMin', { min: REASON_MIN }));
      ok = false;
    } else {
      setReasonError(null);
    }

    if (!ok) return { ok: false };
    return { ok: true, amountCents: cents ?? 0, trimmedReason };
  }

  async function handleSubmit(): Promise<void> {
    const local = validateLocal();
    if (!local.ok) return;

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        amount_cents: local.amountCents,
        reason: local.trimmedReason,
      };
      if (selectedLineItemIds.length > 0) {
        body.line_item_ids = selectedLineItemIds;
      }

      const response = await apiClient.post<IssueCreditNoteResponse>(
        `/api/v1/admin/invoices/${encodeURIComponent(invoiceId)}/credit-notes`,
        body,
        { idempotencyKey: newNoteIdempotencyKey('credit-note') },
      );

      toast({
        variant: 'success',
        title: t('invoice.creditNoteModal.toast.success', {
          number: response.credit_note_number,
        }),
        description: t('invoice.creditNoteModal.toast.successDesc', {
          amount: formatUsdCentsCompact(response.amount_cents),
        }),
      });

      onIssued?.(response);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422 && err.code === 'AMOUNT_EXCEEDS_INVOICE') {
          setAmountError(
            err.message || t('invoice.creditNoteModal.amountExceeds'),
          );
          return;
        }
        if (err.status === 400) {
          toast({
            variant: 'error',
            title: t('invoice.creditNoteModal.toast.invalidTitle'),
            description: err.message,
          });
          return;
        }
        if (err.status === 404) {
          toast({
            variant: 'error',
            title: t('invoice.creditNoteModal.toast.notFoundTitle'),
            description: err.message,
          });
          onOpenChange(false);
          return;
        }
        toast({
          variant: 'error',
          title: t('invoice.creditNoteModal.toast.failTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('invoice.creditNoteModal.toast.networkTitle'),
        description: t('invoice.creditNoteModal.toast.networkDesc'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting ||
    amountDollars.trim() === '' ||
    reason.trim().length < REASON_MIN;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      className="w-full max-w-xl"
      aria-label={t('invoice.creditNoteModal.aria')}
      data-testid="issue-credit-note-modal"
    >
      <DialogHeader>
        <DialogTitle>{t('invoice.creditNoteModal.title')}</DialogTitle>
        <p className="mt-1 text-xs text-pw-text-muted">
          {t('invoice.creditNoteModal.subtitle', {
            label: invoiceLabel ? ` ${invoiceLabel}` : '',
          })}
        </p>
      </DialogHeader>

      <div className="mt-4 max-h-[60vh] overflow-y-auto">
        <NoteFormBase
          amountDollars={amountDollars}
          onAmountDollarsChange={setAmountDollars}
          reason={reason}
          onReasonChange={setReason}
          amountError={amountError}
          reasonError={reasonError}
          disabled={submitting}
          invoiceTotalCentsHint={invoiceTotalCents}
          amountInputTestId="cn-dn-amount-input"
          reasonInputTestId="cn-dn-reason-input"
          extras={
            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-pw-text">
                {t('invoice.creditNoteModal.lineItemsLabel')}
              </label>
              {lineItems.length === 0 ? (
                <p className="rounded-md border border-dashed border-pw-border bg-pw-surface p-3 text-xs text-pw-text-muted">
                  {t('invoice.creditNoteModal.noLineItems')}
                </p>
              ) : (
                <ul
                  className="space-y-1 rounded-md border border-pw-border bg-pw-bg p-2"
                  data-testid="cn-line-item-list"
                >
                  {lineItems.map((li) => {
                    const selected = selectedLineItemIds.includes(li.id);
                    return (
                      <li key={li.id}>
                        <label
                          className={cn(
                            'flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 text-sm',
                            'hover:bg-pw-surface',
                            selected && 'bg-pw-surface',
                          )}
                        >
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              data-testid={`cn-line-item-${li.id}`}
                              className="h-4 w-4 rounded border-pw-border text-pw-primary focus:ring-pw-primary"
                              checked={selected}
                              disabled={submitting}
                              onChange={() => toggleLineItem(li.id)}
                            />
                            <span className="text-pw-text">{li.description}</span>
                            {typeof li.quantity === 'number' ? (
                              <span className="text-xs text-pw-text-muted">
                                × {li.quantity}
                              </span>
                            ) : null}
                          </span>
                          <span className="font-mono text-xs text-pw-text-muted">
                            {formatUsdCentsCompact(li.amount_cents)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
              {selectedLineItemIds.length > 0 ? (
                <p className="text-xs text-pw-text-muted">
                  {t('invoice.creditNoteModal.selectedSummary', {
                    selected: selectedLineItemIds.length,
                    total: lineItems.length,
                    amount: formatUsdCentsCompact(
                      selectedLineItemIds.reduce(
                        (acc, id) =>
                          acc + (lineItemMap.get(id)?.amount_cents ?? 0),
                        0,
                      ),
                    ),
                  })}
                </p>
              ) : null}
            </div>
          }
        />
      </div>

      <DialogFooter className="mt-4 flex justify-end gap-2">
        <Button
          variant="ghost"
          disabled={submitting}
          onClick={() => onOpenChange(false)}
        >
          {t('invoice.creditNoteModal.cancel')}
        </Button>
        <Button
          variant="default"
          disabled={submitDisabled}
          onClick={() => {
            void handleSubmit();
          }}
          data-testid="cn-dn-submit-btn"
        >
          {submitting
            ? t('invoice.creditNoteModal.submitting')
            : t('invoice.creditNoteModal.submit')}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
